import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const ThemeContext = createContext(null);
const BrandingContext = createContext(null);

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

// mode: "dark" | "light" | "system"  ·  effective theme applied to <html data-theme>
export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => localStorage.getItem("mc_theme") || "dark");

  const resolve = useCallback(
    (m) => (m === "system" ? (systemPrefersDark() ? "dark" : "light") : m),
    []
  );
  const [theme, setTheme] = useState(() => resolve(mode));

  useEffect(() => {
    const eff = resolve(mode);
    setTheme(eff);
    document.documentElement.setAttribute("data-theme", eff);
    localStorage.setItem("mc_theme", mode);
  }, [mode, resolve]);

  // follow OS changes while in "system" mode
  useEffect(() => {
    if (mode !== "system" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const eff = systemPrefersDark() ? "dark" : "light";
      setTheme(eff);
      document.documentElement.setAttribute("data-theme", eff);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [mode]);

  const setMode = (m) => setModeState(m);
  const toggle = () => setModeState((m) => (resolve(m) === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, mode, setMode, setTheme: setModeState, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}
export const useTheme = () =>
  useContext(ThemeContext) || { theme: "dark", mode: "dark", setMode: () => {}, toggle: () => {} };

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);
  const apply = (b) => {
    if (!b) return;
    const r = document.documentElement.style;
    if (b.color_primary) r.setProperty("--brand", b.color_primary);
    if (b.color_accent) r.setProperty("--accent-legacy", b.color_accent);
    if (b.app_icon) {
      let link = document.querySelector("link[rel='icon']");
      if (link) link.href = b.app_icon;
    }
  };
  const refresh = () => api.get("/branding").then((r) => { setBranding(r.data); apply(r.data); }).catch(() => {});
  useEffect(() => { refresh(); }, []);
  return <BrandingContext.Provider value={{ branding, refresh }}>{children}</BrandingContext.Provider>;
}
export const useBranding = () => useContext(BrandingContext) || { branding: null, refresh: () => {} };
